import React, { useState, useEffect } from 'react';
import { X, FileText, Video } from 'lucide-react';
import './ReviewContentModal.css';

const ReviewContentModal = ({ isOpen, onClose, type, content }) => {
  const [activeHookTab, setActiveHookTab] = useState(0);
  const [activeCtaTab, setActiveCtaTab] = useState(0);
  
  // Reset tabs when content changes
  useEffect(() => {
    if (content && type === 'script') {
      setActiveHookTab(0);
      setActiveCtaTab(0);
    }
  }, [content, type]);
  
  if (!isOpen || !content) return null;

  const formatBriefText = (text) => {
    if (!text) return '';
    
    // Remove markdown headers (##, ###, etc.)
    let formatted = text.replace(/^#{1,6}\s+/gm, '');
    
    // Convert all-caps headings to title case and replace dashes with bullet points
    formatted = formatted.split('\n').map(line => {
      const trimmed = line.trim();
      
      // Check if line looks like a heading (all caps, not too long, contains letters)
      if (trimmed.length > 0 && 
          trimmed.length < 150 && 
          trimmed === trimmed.toUpperCase() && 
          /[A-Z]/.test(trimmed) && // Contains at least one letter
          !trimmed.startsWith('-') && // Not a bullet point line
          !trimmed.startsWith('•')) {
        // Remove numbers and convert to title case
        let titleCase = trimmed
          .replace(/^\d+\.\s*/, '') // Remove leading numbers with period (e.g., "1. ")
          .replace(/^\d+\s+/, '') // Remove leading numbers with space (e.g., "1 ")
          .split(' ')
          .map(word => {
            if (word.length === 0) return word;
            // Remove numbers from the word
            const cleanedWord = word.replace(/\d+/g, '');
            if (cleanedWord.length === 0) return '';
            // Handle words with special chars
            if (/^[A-Z]+$/.test(cleanedWord)) {
              // Pure uppercase word - convert to title case
              return cleanedWord.charAt(0) + cleanedWord.slice(1).toLowerCase();
            } else if (/^[A-Z:&.,!?\-]+$/.test(cleanedWord)) {
              // Word with special chars - keep structure but lowercase letters after first
              return cleanedWord.charAt(0) + cleanedWord.slice(1).toLowerCase();
            }
            return cleanedWord;
          })
          .filter(word => word.length > 0) // Remove empty words
          .join(' ');
        
        // Preserve original indentation
        const indent = line.match(/^\s*/)?.[0] || '';
        return indent + titleCase;
      }
      
      // Replace dashes at the start of lines with bullet points
      if (trimmed.startsWith('- ')) {
        return line.replace(/^(\s*)-\s/, '$1• ');
      }
      
      return line;
    }).join('\n');
    
    return formatted;
  };

  const renderBrief = () => {
    const brief = content;
    
    // Parse AI response to extract sections
    const parseAIBrief = (aiResponse) => {
      if (!aiResponse) return {};
      
      const sections = {};
      const lines = aiResponse.split('\n');
      let currentSection = '';
      let currentContent = [];
      
      lines.forEach(line => {
        // Check for markdown headers
        if (line.startsWith('## ')) {
          if (currentSection) {
            sections[currentSection] = currentContent.join('\n').trim();
          }
          currentSection = line.replace('## ', '').trim();
          currentContent = [];
        } 
        // Check for numbered sections like "1. BRIEF OVERVIEW"
        else if (/^\d+\.\s+[A-Z]/.test(line.trim())) {
          if (currentSection) {
            sections[currentSection] = currentContent.join('\n').trim();
          }
          currentSection = line.trim();
          currentContent = [];
        }
        // Check for all-caps headers (like "QUICK BRIEF DESCRIPTION")
        else if (/^[A-Z\s]+$/.test(line.trim()) && line.trim().length > 5 && line.trim().length < 100) {
          if (currentSection && currentContent.length === 0) {
            // Skip if we just started a section
          } else {
            if (currentSection) {
              sections[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = line.trim();
            currentContent = [];
          }
        }
        else if (line.trim()) {
          currentContent.push(line.trim());
        }
      });
      
      if (currentSection) {
        sections[currentSection] = currentContent.join('\n').trim();
      }
      
      return sections;
    };
    
    // Get quick brief description
    const getQuickBriefDescription = () => {
      // Try manual sections first
      if (brief.manualBriefSections?.quickBriefDescription) {
        return brief.manualBriefSections.quickBriefDescription;
      }
      
      // Parse AI response
      if (brief.aiResponse) {
        const sections = parseAIBrief(brief.aiResponse);
        return sections['QUICK BRIEF DESCRIPTION'] || sections['Quick Brief Description'];
      }
      
      return null;
    };
    
    // Get brief overview content
    const getBriefOverview = () => {
      const parts = [];
      
      if (brief.goal) {
        parts.push(`Our goal is ${brief.goal}`);
      }
      
      if (brief.audience) {
        parts.push(brief.audience);
      }
      
      if (parts.length > 0) {
        return parts.join('. ');
      }
      
      // Try AI response sections
      if (brief.aiResponse) {
        const sections = parseAIBrief(brief.aiResponse);
        return sections['1. BRIEF OVERVIEW'] || sections['BRIEF OVERVIEW'] || sections['Brief Overview'];
      }
      
      return brief.manualBriefSections?.briefOverview || null;
    };
    
    // Get product/about content
    const getAboutProduct = () => {
      const parts = [];
      
      if (brief.product) {
        parts.push(brief.product);
      }
      
      if (brief.message) {
        parts.push(brief.message);
      }
      
      if (parts.length > 0) {
        return parts.join('. ');
      }
      
      // Try AI response sections
      if (brief.aiResponse) {
        const sections = parseAIBrief(brief.aiResponse);
        return sections['2. ABOUT OUR PRODUCT'] || sections['ABOUT OUR PRODUCT'] || sections['About Our Product'];
      }
      
      return brief.manualBriefSections?.aboutProduct || brief.manualBriefSections?.aboutOurProduct || null;
    };
    
    const quickBrief = getQuickBriefDescription();
    const briefOverview = getBriefOverview();
    const aboutProduct = getAboutProduct();
    
    // Get brief settings/metadata for cards
    const briefSettings = brief.briefSettings || {};
    const platform = briefSettings.platform || brief.platform || null;
    const videoLength = briefSettings.videoLength || brief.videoLength || null;
    
    return (
      <div className="review-content-modal-body-unique">
        {/* Brief Metric Cards */}
        {(brief.product || brief.audience || brief.goal || platform || brief.brandToneStyle) && (
          <div className="review-brief-metrics-unique">
            {brief.product && (
              <div className="review-brief-metric-card-unique review-brief-metric-card-primary-unique">
                <div className="review-brief-metric-icon-unique">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z"/>
                    <path d="M2 17L12 22L22 17"/>
                    <path d="M2 12L12 17L22 12"/>
                  </svg>
                </div>
                <div className="review-brief-metric-content-unique">
                  <div className="review-brief-metric-label-unique">PRODUCT/SERVICE</div>
                  <div className="review-brief-metric-value-unique" title={brief.product}>
                    {brief.product}
                  </div>
                  <div className="review-brief-metric-subtitle-unique">What we're promoting</div>
                </div>
              </div>
            )}
            
            {brief.audience && (
              <div className="review-brief-metric-card-unique">
                <div className="review-brief-metric-icon-unique review-brief-metric-icon-gray-unique">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="review-brief-metric-content-unique">
                  <div className="review-brief-metric-label-unique review-brief-metric-label-gray-unique">TARGET AUDIENCE</div>
                  <div className="review-brief-metric-value-unique review-brief-metric-value-gray-unique" title={brief.audience}>
                    {brief.audience}
                  </div>
                  <div className="review-brief-metric-subtitle-unique review-brief-metric-subtitle-gray-unique">Who we're reaching</div>
                </div>
              </div>
            )}
            
            {brief.goal && (
              <div className="review-brief-metric-card-unique">
                <div className="review-brief-metric-icon-unique review-brief-metric-icon-gray-unique">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div className="review-brief-metric-content-unique">
                  <div className="review-brief-metric-label-unique review-brief-metric-label-gray-unique">GOAL</div>
                  <div className="review-brief-metric-value-unique review-brief-metric-value-gray-unique" title={brief.goal}>
                    {brief.goal}
                  </div>
                  <div className="review-brief-metric-subtitle-unique review-brief-metric-subtitle-gray-unique">Campaign objective</div>
                </div>
              </div>
            )}
            
            {(platform || brief.brandToneStyle) && (
              <div className="review-brief-metric-card-unique">
                <div className="review-brief-metric-icon-unique review-brief-metric-icon-gray-unique">
                  {platform ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                    </svg>
                  )}
                </div>
                <div className="review-brief-metric-content-unique">
                  <div className="review-brief-metric-label-unique review-brief-metric-label-gray-unique">
                    {platform ? 'PLATFORM' : 'BRAND TONE'}
                  </div>
                  <div className="review-brief-metric-value-unique review-brief-metric-value-gray-unique" title={platform || brief.brandToneStyle}>
                    {platform || brief.brandToneStyle}
                  </div>
                  <div className="review-brief-metric-subtitle-unique review-brief-metric-subtitle-gray-unique">
                    {platform ? 'Target platform' : 'Style direction'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Quick Brief Description */}
        {quickBrief && (
          <div className="review-brief-section-unique">
            <h3 className="review-brief-section-title-unique">QUICK BRIEF DESCRIPTION</h3>
            <p className="review-brief-section-content-unique">{quickBrief}</p>
          </div>
        )}
        
        {/* Brief Overview */}
        {briefOverview && (
          <div className="review-brief-section-unique">
            <h3 className="review-brief-section-title-unique">1. BRIEF OVERVIEW</h3>
            <p className="review-brief-section-content-unique">{briefOverview}</p>
          </div>
        )}
        
        {/* About Our Product */}
        {aboutProduct && (
          <div className="review-brief-section-unique">
            <h3 className="review-brief-section-title-unique">2. ABOUT OUR PRODUCT</h3>
            <p className="review-brief-section-content-unique">{aboutProduct}</p>
          </div>
        )}
        
        {/* Additional sections from manual brief or AI response */}
        {brief.manualBriefSections && Object.keys(brief.manualBriefSections).length > 0 && (() => {
          const excludedKeys = ['quickBriefDescription', 'briefOverview', 'aboutProduct', 'aboutOurProduct'];
          const additionalSections = Object.entries(brief.manualBriefSections)
            .filter(([key]) => !excludedKeys.includes(key));
          
          if (additionalSections.length === 0) return null;
          
          let sectionNumber = 3;
          return (
            <>
              {additionalSections.map(([key, value]) => {
                const title = key.split(/(?=[A-Z])/).join(' ').toUpperCase();
                return (
                  <div key={key} className="review-brief-section-unique">
                    <h3 className="review-brief-section-title-unique">
                      {sectionNumber++}. {title}
                    </h3>
                    <p className="review-brief-section-content-unique">{value}</p>
                  </div>
                );
              })}
            </>
          );
        })()}
        
        {/* Fallback: Show full AI response if no sections were parsed */}
        {!quickBrief && !briefOverview && !aboutProduct && brief.aiResponse && (
          <div className="review-brief-section-unique">
            <div className="review-brief-section-content-unique" style={{ whiteSpace: 'pre-wrap' }}>
              {formatBriefText(brief.aiResponse)}
            </div>
          </div>
        )}
        
        {/* Additional details if needed */}
        {(brief.brandToneStyle || brief.requirements) && (
          <>
            {brief.brandToneStyle && (
              <div className="review-brief-section-unique">
                <h3 className="review-brief-section-title-unique">BRAND TONE & STYLE</h3>
                <p className="review-brief-section-content-unique">{brief.brandToneStyle}</p>
              </div>
            )}
            {brief.requirements && (
              <div className="review-brief-section-unique">
                <h3 className="review-brief-section-title-unique">REQUIREMENTS</h3>
                <p className="review-brief-section-content-unique">{brief.requirements}</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderScript = () => {
    const script = content;
    
    // Parse script lines into hooks, body lines, and CTAs
    const hooks = script.lines?.filter(line => line.type === 'hook') || [];
    const bodyLines = script.lines?.filter(line => line.type === 'line') || [];
    const ctas = script.lines?.filter(line => line.type === 'cta') || [];
    
    // Get settings with defaults
    const settings = script.settings || {};
    const videoLength = settings.videoLength || '30';
    const extraHooks = settings.additionalHooks || 0;
    const extraCTAs = settings.additionalCTAs || 0;
    const creativeControl = settings.creativeControl || 'Scripted';
    
    // Format creative control for display
    const getCreativeControlLabel = (control) => {
      if (control === 'exact') return 'Scripted';
      if (control === 'some') return 'Flexible';
      if (control === 'full') return 'Full Control';
      return control;
    };
    
    return (
      <div className="review-content-modal-body-unique">
        {/* Script Settings Boxes */}
        <div className="review-script-settings-boxes-unique">
          <div className="review-script-setting-box-unique review-script-setting-box-time-unique">
            <div className="review-script-setting-icon-unique">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className="review-script-setting-label-unique">TIME</div>
            <div className="review-script-setting-value-unique">{videoLength}s</div>
            <div className="review-script-setting-desc-unique">Video duration</div>
          </div>
          
          <div className="review-script-setting-box-unique review-script-setting-box-white-unique">
            <div className="review-script-setting-icon-unique review-script-setting-icon-gray-unique">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div className="review-script-setting-label-unique review-script-setting-label-gray-unique">EXTRA HOOKS</div>
            <div className="review-script-setting-value-unique review-script-setting-value-gray-unique">{extraHooks}</div>
            <div className="review-script-setting-desc-unique review-script-setting-desc-gray-unique">Additional options</div>
          </div>
          
          <div className="review-script-setting-box-unique review-script-setting-box-white-unique">
            <div className="review-script-setting-icon-unique review-script-setting-icon-gray-unique">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <circle cx="12" cy="12" r="6"/>
              </svg>
            </div>
            <div className="review-script-setting-label-unique review-script-setting-label-gray-unique">EXTRA CTA</div>
            <div className="review-script-setting-value-unique review-script-setting-value-gray-unique">{extraCTAs}</div>
            <div className="review-script-setting-desc-unique review-script-setting-desc-gray-unique">Additional options</div>
          </div>
          
          <div className="review-script-setting-box-unique review-script-setting-box-white-unique">
            <div className="review-script-setting-icon-unique review-script-setting-icon-gray-unique">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
            </div>
            <div className="review-script-setting-label-unique review-script-setting-label-gray-unique">CREATIVE CONTROL</div>
            <div className="review-script-setting-value-unique review-script-setting-value-gray-unique">{getCreativeControlLabel(creativeControl)}</div>
            <div className="review-script-setting-desc-unique review-script-setting-desc-gray-unique">Flexibility level</div>
          </div>
        </div>
        
        {/* Script Content Sections */}
        <div className="review-script-content-sections-unique">
          {/* Hooks Section */}
          {hooks.length > 0 && (
            <div className="review-script-section-unique">
              <div className="review-script-section-header-unique">
                <h3 className="review-script-section-title-unique">
                  {hooks.length > 1 ? 'Hooks' : 'Hook'}
                  {hooks.length > 1 && (
                    <span className="review-script-tab-new-badge-unique">NEW</span>
                  )}
                </h3>
              </div>
              
              {/* Hook Tabs if multiple hooks */}
              {hooks.length > 1 ? (
                <div className="review-script-tabs-unique">
                  {hooks.map((hook, index) => (
                    <button
                      key={index}
                      className={`review-script-tab-unique ${activeHookTab === index ? 'active' : ''}`}
                      onClick={() => setActiveHookTab(index)}
                    >
                      {index === 0 ? 'Original Hook' : `Hook ${index + 1}`}
                      {index > 0 && (
                        <span className="review-script-tab-new-badge-unique">NEW</span>
                      )}
                    </button>
                  ))}
                </div>
              ) : null}
              
              {/* Active Hook Content */}
              {hooks[activeHookTab] && (
                <div className="review-script-line-content-unique">
                  <div className="review-script-line-item-unique">
                    <label className="review-script-line-label-unique">Script</label>
                    <div className="review-script-line-text-unique">
                      {hooks[activeHookTab].text || 'No text provided'}
                    </div>
                  </div>
                  
                  {hooks[activeHookTab].action && (
                    <div className="review-script-line-item-unique">
                      <label className="review-script-line-label-unique">Action</label>
                      <div className="review-script-line-text-unique">
                        {hooks[activeHookTab].action}
                      </div>
                    </div>
                  )}
                  
                  {hooks[activeHookTab].notes && (
                    <div className="review-script-line-item-unique">
                      <label className="review-script-line-label-unique">Notes</label>
                      <div className="review-script-line-text-unique">
                        {hooks[activeHookTab].notes}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Body Lines Section */}
          {bodyLines.length > 0 && (
            <div className="review-script-section-unique">
              <div className="review-script-section-header-unique">
                <h3 className="review-script-section-title-unique">Body</h3>
              </div>
              
              <div className="review-script-body-lines-unique">
                {bodyLines.map((line, index) => (
                  <div key={index} className="review-script-body-line-unique">
                    <div className="review-script-body-line-number-unique">Body {index + 1}</div>
                    <div className="review-script-line-content-unique">
                      <div className="review-script-line-item-unique">
                        <label className="review-script-line-label-unique">Script</label>
                        <div className="review-script-line-text-unique">
                          {line.text || 'No text provided'}
                        </div>
                      </div>
                      
                      {line.action && (
                        <div className="review-script-line-item-unique">
                          <label className="review-script-line-label-unique">Action</label>
                          <div className="review-script-line-text-unique">
                            {line.action}
                          </div>
                        </div>
                      )}
                      
                      {line.notes && (
                        <div className="review-script-line-item-unique">
                          <label className="review-script-line-label-unique">Notes</label>
                          <div className="review-script-line-text-unique">
                            {line.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* CTAs Section */}
          {ctas.length > 0 && (
            <div className="review-script-section-unique">
              <div className="review-script-section-header-unique">
                <h3 className="review-script-section-title-unique">
                  {ctas.length > 1 ? 'CTAs' : 'CTA'}
                  {ctas.length > 1 && (
                    <span className="review-script-tab-new-badge-unique">NEW</span>
                  )}
                </h3>
              </div>
              
              {/* CTA Tabs if multiple CTAs */}
              {ctas.length > 1 ? (
                <div className="review-script-tabs-unique">
                  {ctas.map((cta, index) => (
                    <button
                      key={index}
                      className={`review-script-tab-unique ${activeCtaTab === index ? 'active' : ''}`}
                      onClick={() => setActiveCtaTab(index)}
                    >
                      {index === 0 ? 'Original CTA' : `CTA ${index + 1}`}
                      {index > 0 && (
                        <span className="review-script-tab-new-badge-unique">NEW</span>
                      )}
                    </button>
                  ))}
                </div>
              ) : null}
              
              {/* Active CTA Content */}
              {ctas[activeCtaTab] && (
                <div className="review-script-line-content-unique">
                  <div className="review-script-line-item-unique">
                    <label className="review-script-line-label-unique">Script</label>
                    <div className="review-script-line-text-unique">
                      {ctas[activeCtaTab].text || 'No text provided'}
                    </div>
                  </div>
                  
                  {ctas[activeCtaTab].action && (
                    <div className="review-script-line-item-unique">
                      <label className="review-script-line-label-unique">Action</label>
                      <div className="review-script-line-text-unique">
                        {ctas[activeCtaTab].action}
                      </div>
                    </div>
                  )}
                  
                  {ctas[activeCtaTab].notes && (
                    <div className="review-script-line-item-unique">
                      <label className="review-script-line-label-unique">Notes</label>
                      <div className="review-script-line-text-unique">
                        {ctas[activeCtaTab].notes}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="review-content-modal-overlay-unique" onClick={onClose}>
      <div className="review-content-modal-container-unique" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="review-content-modal-header-unique">
          <div className="review-content-modal-header-content-unique">
            <div className="review-content-modal-icon-unique">
              {type === 'brief' ? (
                <FileText size={24} />
              ) : (
                <Video size={24} />
              )}
            </div>
            <div className="review-content-modal-title-group-unique">
              <h2 className="review-content-modal-title-unique">
                {type === 'brief' 
                  ? (content?.title || content?.name || 'Brief')
                  : (content?.name || content?.title || 'Script')
                }
              </h2>
              <p className="review-content-modal-subtitle-unique">
                {type === 'brief' ? 'Project Brief' : 'Video Script'}
              </p>
            </div>
          </div>
          <button 
            className="review-content-modal-close-unique" 
            onClick={onClose}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Content */}
        <div className="review-content-modal-body-wrapper-unique">
          {type === 'brief' ? renderBrief() : renderScript()}
        </div>

        {/* Footer */}
        <div className="review-content-modal-footer-unique">
          <button 
            className="review-content-modal-close-btn-unique"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewContentModal;

